import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { FixedDaysService } from '@/services/fixedDaysService';
import type { FixedDaysState, FixedDaysPool } from '@/types/fixedDays.types';

const initialState: Omit<
  FixedDaysState,
  | 'fetchPools'
  | 'fetchHistories'
  | 'fetchSummary'
  | 'fetchFile'
  | 'fetchParticipants'
  | 'fetchParticipantsSummary'
  | 'setSelectedPool'
  | 'setSelectedHistory'
  | 'setSelectedParticipant'
  | 'reset'
> = {
  pools: [],
  summaries: [],
  participants: [],
  participantsSummary: {},
  histories: [],
  fileExplanation: null,
  selectedPool: null,
  selectedHistory: null,
  selectedParticipant: null,
  loading: false,
  loadingHistories: false,
  loadingParticipants: false,
  error: null,
};

interface FixedDaysActions {
  fetchPools: (forceRefresh?: boolean, options?: { silent?: boolean }) => Promise<void>;
  fetchHistories: (poolId: number) => Promise<void>;
  fetchSummary: (fileId: number) => Promise<void>;
  fetchFile: (fileId: number) => Promise<void>;
  fetchParticipants: (fileId: number) => Promise<void>;
  fetchParticipantsSummary: (fileId: number, participantName?: string) => Promise<void>;
  setSelectedPool: (pool: FixedDaysPool) => void;
  setSelectedHistory: (fileId: number) => void;
  setSelectedParticipant: (participantName: string | null) => void;
  resetSelections: () => void;
  reset: () => void;
}

export const useFixedDaysStore = create<FixedDaysState & FixedDaysActions>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetchPools: async (_, options) => {
        const silent = options?.silent ?? false;

        if (silent) {
          set({ error: null });
        } else {
          set({ loading: true, error: null });
        }
        try {
          const pools = await FixedDaysService.getPools();
          if (silent) {
            set({ pools });
          } else {
            set({ pools, loading: false });
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch pools';
          if (silent) {
            set({ error: errorMessage });
          } else {
            set({ error: errorMessage, loading: false });
          }
          console.error('Error fetching pools:', error);
        }
      },

      fetchHistories: async (poolId: number) => {
        const state = get();

        // Clear data if fetching for a different pool
        const isDifferentPool = state.selectedPool?.poolId !== poolId;
        if (isDifferentPool) {
          set({ loadingHistories: true, error: null, histories: [], selectedHistory: null });
        } else {
          set({ loadingHistories: true, error: null });
        }
        try {
          const histories = await FixedDaysService.getHistory(poolId);
          // Guard against stale responses if the user switched pools mid-flight.
          // Don't clear loadingHistories here — a newer concurrent fetch may own it.
          if (get().selectedPool?.poolId !== poolId) return;
          set({ histories, loadingHistories: false });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch histories';
          if (get().selectedPool?.poolId !== poolId) return;
          set({ error: errorMessage, loadingHistories: false, histories: [] });
          console.error('Error fetching histories:', error);
        }
      },

      fetchSummary: async (fileId: number) => {
        const state = get();
        const isSameHistoryRequest = state.selectedHistory === fileId;

        // Clear data if fetching for a different history
        const isDifferentHistory = !isSameHistoryRequest;
        if (isDifferentHistory) {
          set({ selectedHistory: fileId, loading: true, error: null, summaries: [] });
        } else {
          set({ selectedHistory: fileId, loading: true, error: null });
        }
        try {
          const summaries = await FixedDaysService.getSummary(fileId);
          // Guard against stale responses if the user switched histories mid-flight
          if (get().selectedHistory !== fileId) return;
          set({ summaries, loading: false });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch summary';
          if (get().selectedHistory !== fileId) return;
          set({ error: errorMessage, loading: false, summaries: [] });
          console.error('Error fetching summary:', error);
        }
      },

      fetchFile: async (fileId: number) => {
        try {
          const file = await FixedDaysService.getFile(fileId);
          // Guard against stale responses if the user switched histories mid-flight
          if (get().selectedHistory !== fileId) return;
          set({ fileExplanation: file?.fileExplanation ?? null });
        } catch (error) {
          // Don't surface this as a page-level error — the explanation is supplemental
          console.error(`Error fetching Fixed Days file for fileId ${fileId}:`, error);
          if (get().selectedHistory === fileId) {
            set({ fileExplanation: null });
          }
        }
      },

      fetchParticipants: async (fileId: number) => {
        const state = get();

        // Clear data if fetching for a different history
        const isDifferentHistory = state.selectedHistory !== fileId;
        if (isDifferentHistory) {
          set({ loadingParticipants: true, error: null, participants: [] });
        } else {
          set({ loadingParticipants: true, error: null });
        }
        try {
          const participants = await FixedDaysService.getParticipants(fileId);
          // Guard against stale responses if the user switched histories mid-flight
          if (get().selectedHistory !== fileId) return;
          set({ participants, loadingParticipants: false });
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to fetch participants';
          if (get().selectedHistory !== fileId) return;
          set({ error: errorMessage, loadingParticipants: false, participants: [] });
          console.error('Error fetching participants:', error);
        }
      },

      fetchParticipantsSummary: async (fileId: number, participantName?: string) => {
        if (!participantName) {
          set({ participantsSummary: {} });
          return;
        }

        set({ loadingParticipants: true, error: null });
        try {
          const participantsSummary = await FixedDaysService.getParticipantsSummary(
            fileId,
            participantName
          );
          // Guard against stale responses if the user switched histories or
          // participants mid-flight
          const currentState = get();
          if (
            currentState.selectedHistory !== fileId ||
            currentState.selectedParticipant !== participantName
          ) {
            return;
          }
          set({ participantsSummary, loadingParticipants: false });
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to fetch participants summary';
          const currentState = get();
          if (
            currentState.selectedHistory !== fileId ||
            currentState.selectedParticipant !== participantName
          ) {
            return;
          }
          set({ error: errorMessage, loadingParticipants: false, participantsSummary: {} });
          console.error('Error fetching participants summary:', error);
        }
      },

      setSelectedPool: (pool: FixedDaysPool) => {
        const state = get();
        // Only reset dependent data if pool actually changed
        if (state.selectedPool?.poolId !== pool.poolId) {
          set({
            selectedPool: pool,
            selectedHistory: null,
            histories: [],
            summaries: [],
            participants: [],
            participantsSummary: {},
            fileExplanation: null,
          });
        } else {
          set({ selectedPool: pool });
        }
      },

      setSelectedHistory: (fileId: number) => {
        const state = get();
        // Only reset detail data if history actually changed
        if (state.selectedHistory !== fileId) {
          set({
            selectedHistory: fileId,
            summaries: [],
            participants: [],
            participantsSummary: {},
            fileExplanation: null,
          });
        }
      },

      setSelectedParticipant: (participantName: string | null) => {
        set({ selectedParticipant: participantName });
      },

      resetSelections: () => {
        set({
          selectedPool: null,
          selectedHistory: null,
          selectedParticipant: null,
          histories: [],
          summaries: [],
          participants: [],
          participantsSummary: {},
          fileExplanation: null,
          loading: false,
          loadingHistories: false,
          loadingParticipants: false,
          error: null,
        });
      },

      reset: () => {
        set({ ...initialState });
      },
    }),
    { name: 'FixedDaysStore' }
  )
);
